import { FeatureCollectionMap } from '@/components/controls';
import { spin } from '@/utils';
import {
  Autorenew as AutorenewIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { bbox } from '@turf/turf';
import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { Objective } from './Objective';
import { useObjectiveFeatureCollection } from './useObjectiveFeatureCollection';

export function Objectives({ title, groupCodes, onComplete }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    query: { data: objectiveFeatureCollection, isFetching, refetch },
  } = useObjectiveFeatureCollection({ groupCodes });
  const mapRef = useRef();
  const hoverTimerRef = useRef();
  const selectedFeature = objectiveFeatureCollection?.features.find(
    (f) => f.properties.identifier === searchParams.get('id'),
  );

  function handleRefreshClick() {
    refetch();
  }

  function handleCompleteClick() {
    onComplete();
  }

  const handleClick = (id) => () => {
    setSearchParams((prev) => ({ ...Object.fromEntries(prev), id }));
  };

  const handleMouseOver = (id) => () => {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      const feature = objectiveFeatureCollection?.features.find(
        (f) => f.properties.identifier === id,
      );
      if (feature && mapRef.current) {
        mapRef.current.fitBounds(bbox(feature), {
          padding: 40,
          duration: 1000,
        });
      }
    }, 1000);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimerRef.current);
  };

  function handleClose() {
    setSearchParams((prev) =>
      Object.fromEntries([...prev.entries()].filter(([key]) => key !== 'id')),
    );
  }

  const handleMapClick = useCallback(
    (event) => {
      setSearchParams((prev) =>
        event.features?.[0]?.properties?.identifier
          ? {
              ...Object.fromEntries(prev),
              id: event.features[0].properties.identifier,
            }
          : {
              ...Object.fromEntries(
                prev.entries().filter(([key]) => key !== 'id'),
              ),
            },
      );
    },
    [setSearchParams],
  );

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 48,
        height: 'calc(100vh - 48px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          flexShrink: 0,
          bgcolor: 'background.default',
          pl: 2,
          pr: 1,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">{title}</Typography>
        <Box>
          <Tooltip title="Done" placement="right">
            <IconButton onClick={handleCompleteClick}>
              <DoneIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box>
          <Tooltip title="Refresh" placement="bottom">
            <Box component="span">
              <IconButton onClick={handleRefreshClick} disabled={isFetching}>
                <AutorenewIcon
                  fontSize="inherit"
                  sx={
                    isFetching
                      ? { animation: `${spin} 2s linear infinite` }
                      : undefined
                  }
                />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Stack>
      <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
        <Stack
          sx={{
            borderRight: 1,
            borderColor: 'divider',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          <List
            dense
            disablePadding
            sx={{
              height: 1,
              width: 240,
              overflowY: 'auto',
            }}
          >
            {objectiveFeatureCollection?.features.map((feature) => (
              <ListItemButton
                dense
                key={feature.properties.identifier}
                selected={
                  searchParams.get('id') === feature.properties.identifier
                }
                onClick={handleClick(feature.properties.identifier)}
                onMouseOver={handleMouseOver(feature.properties.identifier)}
                onMouseLeave={handleMouseLeave}
              >
                <ListItemText primary={feature.properties.title} />
              </ListItemButton>
            ))}
          </List>
        </Stack>
        {selectedFeature ? (
          <Objective feature={selectedFeature} onClose={handleClose} />
        ) : (
          <Box sx={{ flexGrow: 1, height: 1 }}>
            <FeatureCollectionMap
              featureCollection={objectiveFeatureCollection}
              onClick={handleMapClick}
              mapRef={mapRef}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
}
